const EXTENSION_CONTENT_TYPES = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  heif: 'image/heif',
};

const VIDEO_EXTENSIONS = new Set(['mov', 'mp4', 'm4v', 'webm', 'avi', 'mkv', '3gp', 'mpeg', 'mpg']);

/** 50 MB. client-documents and appliance-photos already use this limit. */
export const HOMEOWNER_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

/**
 * iOS WKWebView treats an accept list of image MIME types plus application/pdf
 * as photos-only, so a PDF in Files cannot be chosen. Extensions keep both
 * the Files and Photos pickers. image/jpg is not a real MIME type and is omitted.
 */
export const HOMEOWNER_DOCUMENT_ACCEPT = 'application/pdf,image/jpeg,image/png,image/heic,image/heif,.pdf,.jpg,.jpeg,.png,.heic,.heif';

const DOCUMENT_CONTENT_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
]);

function fileBaseName(fileName) {
  const name = String(fileName || '').trim();
  const slash = Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\'));
  return slash >= 0 ? name.slice(slash + 1) : name;
}

function fileExtension(fileName) {
  const base = fileBaseName(fileName).toLowerCase();
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1);
}

function declaredContentType(file) {
  const declared = file && typeof file.type === 'string' ? file.type.trim().toLowerCase() : '';
  if (declared === 'image/jpg' || declared === 'image/pjpeg') return 'image/jpeg';
  return declared;
}

/**
 * iOS often leaves file.type empty for HEIC and for PDFs chosen from Files.
 * Fall back to the extension so Storage receives a real content type.
 */
export function contentTypeForFile(file) {
  const declared = declaredContentType(file);
  if (declared && declared !== 'application/octet-stream') return declared;
  return EXTENSION_CONTENT_TYPES[fileExtension(file && file.name)] || 'application/octet-stream';
}

/** Title box value: the picked file's name without its extension. */
export function documentTitleFromFileName(fileName) {
  const base = fileBaseName(fileName);
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return base;
  return base.slice(0, dot);
}

/** Keep an edited title. An empty title falls back to the file name. */
export function documentTitleForSave(title, fileName) {
  const typed = String(title || '').trim();
  if (typed) return typed;
  return fileBaseName(fileName);
}

/**
 * Choosing a file fills a blank title from the file name.
 * A title the homeowner already typed is left alone.
 */
export function documentTitleAfterFilePick(currentTitle, fileName) {
  if (String(currentTitle || '').trim()) return String(currentTitle);
  return documentTitleFromFileName(fileName);
}

export function homeownerDocumentFile(file) {
  if (!file) return { ok: false, message: 'Please select a file to upload' };
  const ext = fileExtension(file.name);
  const declared = declaredContentType(file);
  if (declared.startsWith('video/') || VIDEO_EXTENSIONS.has(ext)) {
    return { ok: false, message: "Video files aren't supported. Use a PDF, JPG, PNG, or HEIC file." };
  }
  const contentType = contentTypeForFile(file);
  if (!DOCUMENT_CONTENT_TYPES.has(contentType)) {
    return { ok: false, message: 'Use a PDF, JPG, PNG, or HEIC file.' };
  }
  if (file.size > HOMEOWNER_UPLOAD_MAX_BYTES) {
    return { ok: false, message: 'File size must be 50 MB or less' };
  }
  return { ok: true, contentType };
}

/** Prefer the Storage JSON message over a generic failure string. */
export async function storageErrorMessage(response, fallback = 'Upload failed') {
  const raw = await response.text().catch(() => '');
  const trimmed = raw.trim();
  if (!trimmed) return `${fallback} (${response.status})`;
  try {
    const body = JSON.parse(trimmed);
    const message = body.message || body.error || body.msg;
    if (message) return String(message);
  } catch (error) {
    // Storage sometimes returns plain text.
  }
  return trimmed;
}
